import SyncConflictError from '../utils/SyncConflictError';
import {REGISTRY_CORE_API_HOST} from '../constants/app';
import {LINK_STORE_REGISTRY_ERRORS_LOOKUP} from '../constants';
import apiFetch from './utils/apiFetch';

const RegistryCoreApi = {
  getCouple: async memberId => {
    if (!memberId) {
      throw new Error('RegistryCoreApi.getCouple requires a memberId');
    }
    const options = {
      method: 'GET',
    };
    const url = `${REGISTRY_CORE_API_HOST}/members/${memberId}/summary?eventType=wedding&isIncludedProductSyncInfo=true`;
    const tokenErrorDescription = 'RegistryCoreApi.getCouple requires a token';
    const res = await apiFetch(url, options, tokenErrorDescription);

    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }

    const jsonResponse = await res.json();

    return jsonResponse;
  },
  deleteRegistry: async (registryId, purge = true) => {
    if (!registryId) {
      throw new Error('RegistryCoreApi.deleteRegistry requires a registryId');
    }

    const options = {
      method: 'DELETE',
    };

    const url = `${REGISTRY_CORE_API_HOST}/registries/${registryId}?purge=${purge}`;
    const tokenErrorDescription =
      'RegistryCoreApi.deleteRegistry requires a token';

    const res = await apiFetch(url, options, tokenErrorDescription);

    if (res.status >= 200 && res.status < 300) {
      return;
    }

    const error = new Error(res.statusText);
    error.status = res.status;
    throw error;
  },
  updateRegistry: async (registryId, registryPayload) => {
    if (!registryId) {
      throw new Error('RegistryCoreApi.updateRegistry requires a registryId');
    }
    if (!registryPayload) {
      throw new Error(
        'RegistryCoreApi.updateRegistry requires a registryPayload',
      );
    }
    const tokenErrorDescription =
      'RegistryCoreApi.updateRegistry requires a token';

    const options = {
      method: 'PUT',
      body: JSON.stringify(registryPayload),
    };

    const url = `${REGISTRY_CORE_API_HOST}/registries/${registryId}`;
    const res = await apiFetch(url, options, tokenErrorDescription);

    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }
  },
  updateManualRegistry: async ({
    registryId,
    coupleId,
    storeName,
    registryUrl,
  }) => {
    const manualRegistryPayload = {
      coupleId,
      name: storeName,
      url: registryUrl,
    };

    const options = {
      method: 'PUT',
      body: JSON.stringify(manualRegistryPayload),
    };

    const url = `${REGISTRY_CORE_API_HOST}/manual_registries/${registryId}`;
    const tokenErrorDescription =
      'RegistryCoreApi.updateManualRegistry requires a token';
    const res = await apiFetch(url, options, tokenErrorDescription);

    if (!res.ok) {
      const error = new Error(res.statusText);
      error.status = res.status;
      throw error;
    }
  },
  addManualRegistry: async ({retailerId, coupleId, name, registryUrl}) => {
    const manualRegistryPayload = {
      coupleId,
      name,
      url: registryUrl,
      retailerId: retailerId || undefined,
    };

    const options = {
      method: 'POST',
      body: JSON.stringify(manualRegistryPayload),
    };

    const url = `${REGISTRY_CORE_API_HOST}/manual_registries`;
    const tokenErrorDescription =
      'RegistryCoreApi.addManualRegistry requires a token';
    const res = await apiFetch(url, options, tokenErrorDescription);
    const jsonResponse = await res.json();
    if (res.ok) {
      return jsonResponse;
    }

    // This is a artifact of Web-UI-FE. It's an arbitrary number representing a conflict which has different messaging
    // we know of codes 10001 to 10008
    if (
      Object.keys(LINK_STORE_REGISTRY_ERRORS_LOOKUP).includes(
        JSON.stringify(jsonResponse.errorCode),
      )
    ) {
      throw new SyncConflictError(
        res.statusText,
        jsonResponse.errorCode,
        res.status,
        jsonResponse.data,
      );
    }

    const error = new Error(res.statusText);
    error.status = res.status;
    throw error;
  },
  getRegistryProductsSynchronizationResult: async registryUUID => {
    const options = {
      method: 'GET',
    };

    const url = `${REGISTRY_CORE_API_HOST}/registries/${registryUUID}/product-synchronization`;
    const tokenErrorDescription =
      'RegistryCoreApi.getRegistryProductsSynchronizationResult requires a token';
    const res = await apiFetch(url, options, tokenErrorDescription);
    if (res.ok) {
      const response = await res.json();
      return response;
    }

    const error = new Error(res.statusText);
    error.status = res.status;
    throw error;
  },
  getRegistriesProductsSynchronizationResult: async registries => {
    const options = {
      method: 'GET',
    };
    const searchParams = new URLSearchParams(
      registries.map(registry => ['registryIds', registry.registryUuid]),
    );
    const url = `${REGISTRY_CORE_API_HOST}/registries/product-synchronization?${searchParams}`;
    const tokenErrorDescription =
      'RegistryCoreApi.getRegistriesProductsSynchronizationResult requires a token';
    const res = await apiFetch(url, options, tokenErrorDescription);
    if (res.ok) {
      const response = await res.json();
      return response;
    }

    const error = new Error(res.statusText);
    error.status = res.status;
    throw error;
  },
  getRegistryVerificationEmails: async registryUUID => {
    const options = {
      method: 'GET',
    };

    const url = `${REGISTRY_CORE_API_HOST}/registries/${registryUUID}/emails`;
    const tokenErrorDescription =
      'RegistryCoreApi.getRegistryVerificationEmails requires a token';
    const res = await apiFetch(url, options, tokenErrorDescription);
    if (res.ok) {
      const response = await res.json();
      return response;
    }

    const error = new Error(res.statusText);
    error.status = res.status;
    throw error;
  },
  relinkRegistry: async (registryUUID, email) => {
    const relinkRegistryPayload = {email};

    const options = {
      method: 'PUT',
      body: JSON.stringify(relinkRegistryPayload),
    };

    const url = `${REGISTRY_CORE_API_HOST}/manual_registries/${registryUUID}/relink`;
    const tokenErrorDescription =
      'RegistryCoreApi.relinkRegistry requires a token';
    const res = await apiFetch(url, options, tokenErrorDescription);
    const jsonResponse = await res.json();
    if (res.ok) {
      return jsonResponse;
    }

    const error = new Error(res.statusText);
    error.status = res.status;
    throw error;
  },
  createTickets: async body => {
    const options = {
      method: 'POST',
      body: JSON.stringify(body),
    };

    const url = `${REGISTRY_CORE_API_HOST}/tickets`;
    const tokenErrorDescription =
      'RegistryCoreApi.relinkRegistry requires a token';
    const res = await apiFetch(url, options, tokenErrorDescription);
    if (res.ok) {
      return res;
    }

    const error = new Error(res.statusText);
    error.status = res.status;
    throw error;
  },
};

export default RegistryCoreApi;
